import { hFetch } from './hFetch';
import type { ContactEventStatus } from './entity_types/ContactEvent';
import type { UserTaskEntity } from './entity_types/UserTask';

export type DashboardMetricsTimeline = 'All Time' | 'This Year' | 'Last Month' | 'This Month' | 'Custom';

export type DashboardMeeting = {
  id: number;
  contact_event_id: number;
  name: string;
  start_time: string;
  end_time: string;
  event_type: 'phone_screen' | 'interview' | 'pipeline_review';
  contact_id: number;
  status: ContactEventStatus;
  location?: string | null;
  interviewer_name?: string | null;
};

export type DashboardUnreadMessage = {
  contact_id: number;
  name: string;
  body: string;
  created_at: string;
  unread_count: number;
};

export type DashboardRecruitingFunnel = {
  sourced: number;
  phone_screened: number;
  interviewed: number;
  hired: number;
  phone_screen_rate: number;
  interview_rate: number;
  hire_rate: number;
};

export type DashboardPipelineReviews = {
  completed: number;
  completion_rate: number;
  score_distribution: Record<'1' | '2' | '3' | '4' | '5', number>;
  unscored: number;
};

export type DashboardResponse = {
  meetings: {
    selected_date: string;
    records: DashboardMeeting[];
  };
  user_tasks: {
    total_incomplete: number;
    records: UserTaskEntity[];
  };
  unread_messages: {
    total_unread_threads: number;
    records: DashboardUnreadMessage[];
  };
  recruiting_funnel: DashboardRecruitingFunnel;
  pipeline_reviews: DashboardPipelineReviews;
  eligible_pipeline_reviewers: string[];
};

type GetDashboardArgs = {
  meetingDate: string;
  recruitingMetricsTimeline: DashboardMetricsTimeline;
  pipelineMetricsTimeline: DashboardMetricsTimeline;
  pipelineReviewer: string;
  recruitingCustomStart?: string | null;
  recruitingCustomEnd?: string | null;
  pipelineCustomStart?: string | null;
  pipelineCustomEnd?: string | null;
};

export const getDashboard = async ({
  meetingDate,
  recruitingMetricsTimeline,
  pipelineMetricsTimeline,
  pipelineReviewer,
  recruitingCustomStart,
  recruitingCustomEnd,
  pipelineCustomStart,
  pipelineCustomEnd,
}: GetDashboardArgs) => {
  const query = new URLSearchParams({
    meeting_date: meetingDate,
    recruiting_metrics_time_constraint: recruitingMetricsTimeline,
    pipeline_metrics_time_constraint: pipelineMetricsTimeline,
    pipeline_reviewer: pipelineReviewer,
  });

  if (recruitingMetricsTimeline === 'Custom' && recruitingCustomStart && recruitingCustomEnd) {
    query.set('recruiting_custom_start', recruitingCustomStart);
    query.set('recruiting_custom_end', recruitingCustomEnd);
  }

  if (pipelineMetricsTimeline === 'Custom' && pipelineCustomStart && pipelineCustomEnd) {
    query.set('pipeline_custom_start', pipelineCustomStart);
    query.set('pipeline_custom_end', pipelineCustomEnd);
  }

  return (await hFetch<DashboardResponse>({
    url: `/api/dashboard?${query.toString()}`,
  })) as DashboardResponse;
};
